import React, { createContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  clearStoredAccessToken,
  getCompanyFromContextResponse,
  getStoredAccessToken,
  getSupervisorAccessToken,
  ensureSupervisorAccessToken,
  refreshAccessToken,
  resolveAccessTokenAfterCompanyContext,
  restoreAccessTokenFromSession,
  setStoredAccessToken,
  subscribeAuthSession,
} from '../api/authToken';
import { createCompanyContextRequest } from '../api/authApi';
import {
  clearCompanySession,
  getHomePath,
  getPlatformAuthToken,
  getSelectedCompanyName,
  setPlatformAuthToken,
  setSelectedCompany,
} from '../api/companySession';
import { AbilityProvider } from '../casl/AbilityContext';
import { createEmptyAbility, defineAbilityFor } from '../casl/defineAbility';
import { fetchCurrentUserPermissions } from '../casl/fetchCurrentUserPermissions';
import { fetchCurrentUserProfile } from '../api/currentUser';
import {
  getCompanyIdFromToken,
  isPlatformSupervisorToken,
} from '../casl/jwtUtils';

export const AuthContext = createContext();

const readSessionFromToken = (token) => {
  if (!token) {
    return {
      isPlatformSupervisor: false,
      isInCompanyContext: false,
      activeCompanyId: null,
      activeCompanyName: null,
    };
  }

  const jwtCompanyId = getCompanyIdFromToken(token);
  const isPlatformSupervisor = isPlatformSupervisorToken(token);
  const isInCompanyContext =
    isPlatformSupervisor && jwtCompanyId != null && jwtCompanyId > 0;
  const activeCompanyId = jwtCompanyId;

  return {
    isPlatformSupervisor,
    isInCompanyContext,
    activeCompanyId,
    activeCompanyName: isInCompanyContext ? getSelectedCompanyName() : null,
  };
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => !!getStoredAccessToken()
  );
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [ability, setAbility] = useState(createEmptyAbility);
  const [session, setSession] = useState(() =>
    readSessionFromToken(getStoredAccessToken())
  );
  const [currentUser, setCurrentUser] = useState(null);
  const loginInProgressRef = useRef(false);
  const currentUserRequestRef = useRef(0);

  const applyPermissionNames = useCallback((permissionNames) => {
    setAbility(defineAbilityFor(permissionNames));
  }, []);

  const applyTokenSession = useCallback((token) => {
    setSession(readSessionFromToken(token));
  }, []);

  const loadCurrentUser = useCallback(async (authToken = null) => {
    const requestId = ++currentUserRequestRef.current;
    const token = authToken ?? getStoredAccessToken();

    if (!token) {
      setCurrentUser(null);
      return;
    }

    try {
      const profile = await fetchCurrentUserProfile(token);
      if (requestId === currentUserRequestRef.current) {
        setCurrentUser(profile);
      }
    } catch {
      if (requestId === currentUserRequestRef.current) {
        setCurrentUser(null);
      }
    }
  }, []);

  const loadAbilities = useCallback(
    async (authToken = null) => {
      const token = authToken ?? getStoredAccessToken();

      if (!token) {
        setAbility(createEmptyAbility());
        return;
      }

      try {
        const names = await fetchCurrentUserPermissions(token);
        applyPermissionNames(names);
      } catch {
        applyPermissionNames([]);
      }
    },
    [applyPermissionNames]
  );

  useEffect(() => {
    return subscribeAuthSession((token) => {
      setIsAuthenticated(!!token);
      applyTokenSession(token);
      if (token) {
        loadCurrentUser(token);
      } else {
        currentUserRequestRef.current += 1;
        setCurrentUser(null);
      }
    });
  }, [applyTokenSession, loadCurrentUser]);

  useEffect(() => {
    let cancelled = false;

    const initializeAuth = async () => {
      if (loginInProgressRef.current) {
        return;
      }

      const token = await restoreAccessTokenFromSession();
      setIsAuthenticated(!!token);
      applyTokenSession(token);

      if (token) {
        await Promise.all([loadAbilities(token), loadCurrentUser(token)]);
      } else {
        clearStoredAccessToken();
        setAbility(createEmptyAbility());
        setCurrentUser(null);
      }

      if (!cancelled) {
        setIsAuthReady(true);
      }
    };

    initializeAuth();

    return () => {
      cancelled = true;
    };
  }, [applyTokenSession, loadAbilities, loadCurrentUser]);

  const login = useCallback(
    async (token) => {
      if (!token) {
        return;
      }

      loginInProgressRef.current = true;
      setIsAuthReady(false);

      try {
        clearCompanySession();
        setStoredAccessToken(token);
        if (getCompanyIdFromToken(token) === 0) {
          setPlatformAuthToken(token);
        }
        applyTokenSession(token);
        await Promise.all([loadAbilities(token), loadCurrentUser(token)]);
      } catch {
        setAbility(createEmptyAbility());
      } finally {
        loginInProgressRef.current = false;
        setIsAuthenticated(true);
        setIsAuthReady(true);
      }
    },
    [applyTokenSession, loadAbilities, loadCurrentUser]
  );

  const logout = useCallback(() => {
    currentUserRequestRef.current += 1;
    clearStoredAccessToken();
    setIsAuthenticated(false);
    setSession(readSessionFromToken(null));
    setCurrentUser(null);
    setAbility(createEmptyAbility());
  }, []);

  const enterCompanyContext = useCallback(async (companyId, companyName) => {
    const id = Number(companyId);
    if (!Number.isInteger(id) || id <= 0) {
      throw new Error('Invalid company id');
    }

    let platformToken = await ensureSupervisorAccessToken();
    if (!platformToken) {
      throw new Error('Nedostaje token supervizora za ulazak u kompaniju');
    }

    let response;
    try {
      response = await createCompanyContextRequest(id, platformToken);
    } catch (error) {
      if (error?.response?.status !== 401) {
        throw error;
      }

      platformToken = await refreshAccessToken();
      if (getCompanyIdFromToken(platformToken) !== 0) {
        throw error;
      }

      response = await createCompanyContextRequest(id, platformToken);
    }

    const contextToken = resolveAccessTokenAfterCompanyContext(response, id);

    const company = getCompanyFromContextResponse(response.data);
    const resolvedName = company?.name ?? companyName ?? null;
    setPlatformAuthToken(platformToken);
    setSelectedCompany(id, resolvedName);
    setStoredAccessToken(contextToken);
    applyTokenSession(contextToken);
    await Promise.all([loadAbilities(contextToken), loadCurrentUser(contextToken)]);
    return company ?? { company_id: id, name: resolvedName };
  }, [applyTokenSession, loadAbilities, loadCurrentUser]);

  const exitCompanyContext = useCallback(async () => {
    const currentToken = getStoredAccessToken();
    if (getCompanyIdFromToken(currentToken) === 0) {
      clearCompanySession();
      setPlatformAuthToken(currentToken);
      applyTokenSession(currentToken);
      return;
    }

    let platformToken = getPlatformAuthToken();
    clearCompanySession();

    if (!platformToken || getCompanyIdFromToken(platformToken) !== 0) {
      try {
        platformToken = await refreshAccessToken();
      } catch {
        platformToken = getStoredAccessToken();
      }
    }

    if (getCompanyIdFromToken(platformToken) !== 0) {
      platformToken = getSupervisorAccessToken() ?? platformToken;
    }

    if (platformToken) {
      setStoredAccessToken(platformToken);
      if (getCompanyIdFromToken(platformToken) === 0) {
        setPlatformAuthToken(platformToken);
      }
      applyTokenSession(platformToken);
      await Promise.all([loadAbilities(platformToken), loadCurrentUser(platformToken)]);
      return;
    }

    const storedToken = getStoredAccessToken();
    applyTokenSession(storedToken);
    await Promise.all([loadAbilities(storedToken), loadCurrentUser(storedToken)]);
  }, [applyTokenSession, loadAbilities, loadCurrentUser]);

  const homePath = getHomePath(session);
  const canLoadDispatcherData =
    isAuthenticated &&
    (!session.isPlatformSupervisor || session.isInCompanyContext);

  const authValue = useMemo(
    () => ({
      isAuthenticated,
      isAuthReady,
      isPlatformSupervisor: session.isPlatformSupervisor,
      isInCompanyContext: session.isInCompanyContext,
      activeCompanyId: session.activeCompanyId,
      activeCompanyName: session.activeCompanyName,
      currentUser,
      canLoadDispatcherData,
      homePath,
      login,
      logout,
      enterCompanyContext,
      exitCompanyContext,
    }),
    [
      isAuthenticated,
      isAuthReady,
      session,
      currentUser,
      canLoadDispatcherData,
      homePath,
      login,
      logout,
      enterCompanyContext,
      exitCompanyContext,
    ]
  );

  return (
    <AuthContext.Provider value={authValue}>
      <AbilityProvider value={ability}>{children}</AbilityProvider>
    </AuthContext.Provider>
  );
};
